import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useAppDialog } from '@/hooks/useAppDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EmptyState } from '@/components/common/EmptyState';
import { TableSkeleton } from '@/components/common/TableSkeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, Search, RefreshCw, Package, AlertTriangle, Wrench, MapPin,
  DollarSign, Calendar, User, Home, Trash2, Edit3, Eye, ChevronLeft,
  Shield, Clock, CheckCircle, XCircle, Filter, BarChart3, QrCode, Printer
} from 'lucide-react';

const CATEGORIES = ['Vehicle', 'Medical Equipment', 'Mobility Equipment', 'IT Equipment', 'Furniture', 'Appliance', 'Safety Equipment', 'Property Fixture', 'Office Equipment', 'Other'];
const STATUSES = ['ACTIVE', 'MAINTENANCE', 'RETIRED', 'LOST', 'DISPOSED'];
const CONDITIONS = ['NEW', 'GOOD', 'FAIR', 'POOR', 'DAMAGED'];
const LOCATION_TYPES = ['PROPERTY', 'PARTICIPANT', 'STAFF', 'OFFICE', 'STORAGE'];

const STATUS_COLORS = {
  ACTIVE: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  MAINTENANCE: 'bg-amber-100 text-amber-700 border-amber-200',
  RETIRED: 'bg-slate-100 text-slate-600 border-slate-200',
  LOST: 'bg-red-100 text-red-700 border-red-200',
  DISPOSED: 'bg-slate-200 text-slate-500 border-slate-300',
};
const COND_COLORS = {
  NEW: 'bg-blue-100 text-blue-700', GOOD: 'bg-green-100 text-green-700',
  FAIR: 'bg-yellow-100 text-yellow-700', POOR: 'bg-orange-100 text-orange-700',
  DAMAGED: 'bg-red-100 text-red-700',
};

const emptyForm = {
  asset_name: '', asset_code: '', category: 'Other', asset_type: '', description: '',
  serial_number: '', supplier: '', purchase_date: '', purchase_cost: '', current_value: '',
  warranty_expiry: '', status: 'ACTIVE', condition: 'NEW', location_type: '',
  property_id: '', participant_id: '', staff_id: '', assigned_to: '', notes: '',
  maintenance_due_date: '', last_maintenance_date: '', maintenance_frequency: '',
  registration_expiry: '', insurance_expiry: '', calibration_due: '', safety_check_due: '',
  state_id: '', branch_id: '',
};

// ═══════════════════════════════════════════════════════════════
// ASSET FORM DIALOG
// ═══════════════════════════════════════════════════════════════
function AssetFormDialog({ open, onClose, onSaved, asset, houses, participants, staff }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const isEdit = !!asset;

  useEffect(() => {
    if (asset) {
      const f = { ...emptyForm };
      Object.keys(f).forEach(k => { if (asset[k] !== undefined && asset[k] !== null) f[k] = String(asset[k]); });
      setForm(f);
    } else {
      setForm(emptyForm);
    }
  }, [asset, open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.asset_name) { toast.error('Asset name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.purchase_cost) payload.purchase_cost = parseFloat(payload.purchase_cost);
      if (payload.current_value) payload.current_value = parseFloat(payload.current_value);
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });

      if (isEdit) {
        await api.put(`/asset-management/assets/${asset.id}`, payload);
        toast.success('Asset updated');
      } else {
        await api.post('/asset-management/assets', payload);
        toast.success('Asset created');
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="asset-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5" /> {isEdit ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
          <DialogDescription>Fill in the asset details below</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Asset Name *</Label>
              <Input value={form.asset_name} onChange={e => set('asset_name', e.target.value)} data-testid="asset-name" /></div>
            <div className="space-y-1"><Label className="text-xs">Asset Code</Label>
              <Input value={form.asset_code} onChange={e => set('asset_code', e.target.value)} placeholder="Auto-generated" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs">Category</Label>
              <Select value={form.category} onValueChange={v => set('category', v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={v => set('status', v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Condition</Label>
              <Select value={form.condition} onValueChange={v => set('condition', v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Serial Number</Label><Input value={form.serial_number} onChange={e => set('serial_number', e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Supplier</Label><Input value={form.supplier} onChange={e => set('supplier', e.target.value)} /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} className="text-xs" /></div>

          <Separator />
          {/* Financial */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs">Purchase Date</Label><Input type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Purchase Cost ($)</Label><Input type="number" value={form.purchase_cost} onChange={e => set('purchase_cost', e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Current Value ($)</Label><Input type="number" value={form.current_value} onChange={e => set('current_value', e.target.value)} /></div>
          </div>

          <Separator />
          {/* Location */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Location Type</Label>
              <Select value={form.location_type || 'none'} onValueChange={v => set('location_type', v === 'none' ? '' : v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Not Set</SelectItem>{LOCATION_TYPES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Property / House</Label>
              <Select value={form.property_id || 'none'} onValueChange={v => set('property_id', v === 'none' ? '' : v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>

          <Separator />
          {/* Maintenance & Expiry Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Warranty Expiry</Label><Input type="date" value={form.warranty_expiry} onChange={e => set('warranty_expiry', e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Maintenance Due</Label><Input type="date" value={form.maintenance_due_date} onChange={e => set('maintenance_due_date', e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Registration Expiry</Label><Input type="date" value={form.registration_expiry} onChange={e => set('registration_expiry', e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Insurance Expiry</Label><Input type="date" value={form.insurance_expiry} onChange={e => set('insurance_expiry', e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Calibration Due</Label><Input type="date" value={form.calibration_due} onChange={e => set('calibration_due', e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Safety Check Due</Label><Input type="date" value={form.safety_check_due} onChange={e => set('safety_check_due', e.target.value)} /></div>
          </div>

          <div className="space-y-1"><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} className="text-xs" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-asset-btn">{saving ? 'Saving...' : isEdit ? 'Update Asset' : 'Create Asset'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════
// ASSET DETAIL DRAWER
// ═══════════════════════════════════════════════════════════════
function AssetDetailDrawer({ asset, open, onClose, onUpdate, permissions }) {
  const [tab, setTab] = useState('overview');
  const [maintenance, setMaintenance] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [maintForm, setMaintForm] = useState({ description: '', contractor_name: '', service_cost: '', service_date: '', next_due_date: '', service_notes: '' });
  const [addingMaint, setAddingMaint] = useState(false);

  const canMaint = permissions.includes('assets.maintenance');
  const canPrintLabel = permissions.includes('assets.label.print');

  const handlePrintLabel = useCallback(async () => {
    try {
      const r = await api.get(`/asset-management/assets/${asset.id}/qr/label.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `asset_label_${asset.asset_code || asset.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Label PDF downloaded');
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e?.response?.data?.detail || 'Failed to print label';
      toast.error(msg);
    }
  }, [asset]);

  useEffect(() => {
    if (asset && open) {
      api.get(`/asset-management/assets/${asset.id}/maintenance`).then(r => setMaintenance(r.data.items || [])).catch(() => {});
      api.get(`/asset-management/assets/${asset.id}/assignment-history`).then(r => setAssignments(r.data.items || [])).catch(() => {});
      api.get(`/audit?entity=asset&entity_id=${asset.id}`).then(r => setAuditLogs(r.data.items || r.data.data || [])).catch(() => setAuditLogs([]));
    }
  }, [asset, open]);

  if (!asset) return null;

  const handleAddMaintenance = async () => {
    setAddingMaint(true);
    try {
      const payload = { ...maintForm, status: 'COMPLETED', maintenance_type: 'SCHEDULED' };
      if (payload.service_cost) payload.service_cost = parseFloat(payload.service_cost);
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      await api.post(`/asset-management/assets/${asset.id}/maintenance`, payload);
      toast.success('Maintenance recorded');
      const r = await api.get(`/asset-management/assets/${asset.id}/maintenance`);
      setMaintenance(r.data.items || []);
      setMaintForm({ description: '', contractor_name: '', service_cost: '', service_date: '', next_due_date: '', service_notes: '' });
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setAddingMaint(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:w-[540px] overflow-y-auto" data-testid="asset-detail-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Package className="w-4 h-4" /> {asset.asset_name}</SheetTitle>
        </SheetHeader>
        <div className="mt-3">
          <div className="flex items-center gap-2 mb-3">
            <Badge className={`${STATUS_COLORS[asset.status] || ''} border text-xs`}>{asset.status}</Badge>
            <Badge className={`${COND_COLORS[asset.condition] || ''} text-xs`}>{asset.condition}</Badge>
            <span className="text-xs text-muted-foreground">{asset.asset_code}</span>
            {canPrintLabel && (
              <Button
                size="sm"
                variant="outline"
                className="ml-auto h-7 text-xs gap-1"
                onClick={handlePrintLabel}
                data-testid="asset-print-qr-label-button"
              >
                <QrCode className="w-3 h-3" /> Print QR Label
              </Button>
            )}
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
              <TabsTrigger value="maintenance" className="text-xs">Maintenance</TabsTrigger>
              <TabsTrigger value="history" className="text-xs">History</TabsTrigger>
              <TabsTrigger value="audit" className="text-xs">Audit</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-3 mt-3">
              <Card><CardContent className="p-3 text-sm space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div><p className="text-muted-foreground text-xs">Category</p><p className="font-medium">{asset.category}</p></div>
                  <div><p className="text-muted-foreground text-xs">Serial</p><p className="font-medium">{asset.serial_number || '—'}</p></div>
                  <div><p className="text-muted-foreground text-xs">Supplier</p><p className="font-medium">{asset.supplier || '—'}</p></div>
                  <div><p className="text-muted-foreground text-xs">Location</p><p className="font-medium">{asset.location_type || '—'}</p></div>
                </div>
                {asset.description && <div><p className="text-muted-foreground text-xs">Description</p><p className="text-xs">{asset.description}</p></div>}
                {asset.property_name && <div><p className="text-muted-foreground text-xs">Property</p><p className="font-medium">{asset.property_name}</p></div>}
                {asset.participant_name && <div><p className="text-muted-foreground text-xs">Participant</p><p className="font-medium">{asset.participant_name}</p></div>}
                {asset.staff_name && <div><p className="text-muted-foreground text-xs">Staff</p><p className="font-medium">{asset.staff_name}</p></div>}
              </CardContent></Card>
              <Card><CardContent className="p-3 text-sm space-y-2">
                <p className="font-medium text-xs text-muted-foreground uppercase tracking-wide">Financial</p>
                <div className="grid grid-cols-3 gap-2">
                  <div><p className="text-muted-foreground text-xs">Purchase Date</p><p className="font-medium">{asset.purchase_date || '—'}</p></div>
                  <div><p className="text-muted-foreground text-xs">Cost</p><p className="font-medium">${asset.purchase_cost || '—'}</p></div>
                  <div><p className="text-muted-foreground text-xs">Current Value</p><p className="font-medium">${asset.current_value || '—'}</p></div>
                </div>
              </CardContent></Card>
              <Card><CardContent className="p-3 text-sm space-y-2">
                <p className="font-medium text-xs text-muted-foreground uppercase tracking-wide">Key Dates</p>
                <div className="grid grid-cols-2 gap-2">
                  {[['Warranty Expiry', asset.warranty_expiry], ['Maintenance Due', asset.maintenance_due_date],
                    ['Registration Expiry', asset.registration_expiry], ['Insurance Expiry', asset.insurance_expiry],
                    ['Calibration Due', asset.calibration_due], ['Safety Check Due', asset.safety_check_due]].map(([label, val]) => (
                    <div key={label}><p className="text-muted-foreground text-xs">{label}</p><p className={`font-medium ${val && val < new Date().toISOString().slice(0,10) ? 'text-red-600' : ''}`}>{val || '—'}</p></div>
                  ))}
                </div>
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="maintenance" className="space-y-3 mt-3">
              {canMaint && (
                <Card><CardContent className="p-3 space-y-2">
                  <p className="text-xs font-medium">Record Maintenance</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Description" value={maintForm.description} onChange={e => setMaintForm(f => ({ ...f, description: e.target.value }))} className="text-xs h-8" />
                    <Input placeholder="Contractor" value={maintForm.contractor_name} onChange={e => setMaintForm(f => ({ ...f, contractor_name: e.target.value }))} className="text-xs h-8" />
                    <Input type="number" placeholder="Cost ($)" value={maintForm.service_cost} onChange={e => setMaintForm(f => ({ ...f, service_cost: e.target.value }))} className="text-xs h-8" />
                    <Input type="date" value={maintForm.service_date} onChange={e => setMaintForm(f => ({ ...f, service_date: e.target.value }))} className="text-xs h-8" />
                  </div>
                  <Input type="date" placeholder="Next due date" value={maintForm.next_due_date} onChange={e => setMaintForm(f => ({ ...f, next_due_date: e.target.value }))} className="text-xs h-8" />
                  <Button size="sm" onClick={handleAddMaintenance} disabled={addingMaint} className="w-full">{addingMaint ? 'Saving...' : 'Record Maintenance'}</Button>
                </CardContent></Card>
              )}
              {maintenance.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No maintenance records</p> : (
                maintenance.map(m => (
                  <Card key={m.id}><CardContent className="p-3 text-xs space-y-1">
                    <div className="flex justify-between"><span className="font-medium">{m.description || m.maintenance_type}</span><span className="text-muted-foreground">{m.service_date || m.created_at?.slice(0, 10)}</span></div>
                    {m.contractor_name && <p className="text-muted-foreground">Contractor: {m.contractor_name}</p>}
                    {m.service_cost && <p>Cost: ${m.service_cost}</p>}
                    <p className="text-muted-foreground">By: {m.performed_by_name}</p>
                  </CardContent></Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="history" className="space-y-3 mt-3">
              {assignments.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No assignment history</p> : (
                assignments.map(a => (
                  <Card key={a.id}><CardContent className="p-3 text-xs space-y-1">
                    <div className="flex justify-between"><Badge variant="outline" className="text-xs">{a.action}</Badge><span className="text-muted-foreground">{a.created_at?.slice(0, 10)}</span></div>
                    <p>Location: {a.location_type}</p>
                    {a.notes && <p className="text-muted-foreground">{a.notes}</p>}
                    <p className="text-muted-foreground">By: {a.performed_by_name}</p>
                  </CardContent></Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="audit" className="space-y-3 mt-3">
              {auditLogs.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No audit logs</p> : (
                auditLogs.map((log, i) => (
                  <div key={i} className="text-xs border-b pb-2">
                    <div className="flex justify-between"><span className="font-medium">{log.action || log.event}</span><span className="text-muted-foreground">{(log.timestamp || log.created_at || '').slice(0, 16)}</span></div>
                  </div>
                ))
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export default function AssetManagementPage() {
  const { dangerConfirm } = useAppDialog();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canCreate = permissions.includes('assets.create');
  const canDelete = permissions.includes('assets.delete');
  const canEdit = permissions.includes('assets.update');
  const canPrintLabel = permissions.includes('assets.label.print');

  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [conditionFilter, setConditionFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [detailAsset, setDetailAsset] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [houses, setHouses] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, search: search || undefined };
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (conditionFilter !== 'all') params.condition = conditionFilter;

      const [aR, sR, alR, hR, pR, stR] = await Promise.all([
        api.get('/asset-management/assets', { params }),
        api.get('/asset-management/stats'),
        api.get('/asset-management/alerts'),
        api.get('/houses', { params: { limit: 100 } }).catch(() => ({ data: { items: [], data: [] } })),
        api.get('/participants', { params: { limit: 200 } }).catch(() => ({ data: { items: [], data: [] } })),
        api.get('/staff', { params: { limit: 200 } }).catch(() => ({ data: { items: [], data: [] } })),
      ]);

      setAssets(aR.data.items || []);
      setTotal(aR.data.total || 0);
      setStats(sR.data || {});
      setAlerts(alR.data.items || []);
      setHouses(hR.data.items || hR.data.data || []);
      setParticipants(pR.data.items || pR.data.data || []);
      setStaffList(stR.data.items || stR.data.data || []);
    } catch (e) { toast.error('Failed to load assets'); }
    finally { setLoading(false); }
  }, [page, search, categoryFilter, statusFilter, conditionFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id, name) => {
    const ok = await dangerConfirm({
      title: 'Delete asset?',
      description: `"${name}" will be permanently removed from the asset register. This cannot be undone.`,
      confirmLabel: 'Delete asset',
      testId: 'asset-delete-confirm',
    });
    if (!ok) return;
    try { await api.delete(`/asset-management/assets/${id}`); toast.success('Deleted'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleView = (asset) => { setDetailAsset(asset); setDetailOpen(true); };
  const handleEdit = (asset) => { setEditAsset(asset); setFormOpen(true); };

  return (
    <div className="space-y-4 p-4 animate-fade-in" data-testid="asset-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Package className="w-6 h-6" /> Asset Management</h1>
          <p className="text-sm text-muted-foreground">Track, maintain and manage all organisational assets</p>
        </div>
        <div className="flex gap-2">
          {alerts.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowAlerts(!showAlerts)} className="relative" data-testid="alerts-toggle">
              <AlertTriangle className="w-4 h-4 mr-1 text-amber-500" /> Alerts
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">{alerts.length}</span>
            </Button>
          )}
          {canPrintLabel && assets.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const ids = assets.slice(0, 100).map(a => a.id);
                  const r = await api.post(
                    '/asset-management/labels/sheet',
                    { asset_ids: ids, layout: 'L7163' },
                    { responseType: 'blob' }
                  );
                  const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `asset_labels_sheet_${ids.length}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  const printed = r.headers?.['x-stickers-printed'] || ids.length;
                  const skipped = r.headers?.['x-stickers-skipped'] || 0;
                  toast.success(`Sheet downloaded — ${printed} stickers (${skipped} skipped)`);
                } catch (e) {
                  const msg = e?.response?.data?.error?.message || e?.response?.data?.detail || 'Bulk print failed';
                  toast.error(msg);
                }
              }}
              data-testid="bulk-print-labels-btn"
            >
              <Printer className="w-4 h-4 mr-1" /> Print Labels ({Math.min(assets.length, 100)})
            </Button>
          )}
          {canCreate && (
            <Button size="sm" onClick={() => { setEditAsset(null); setFormOpen(true); }} data-testid="add-asset-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Asset
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-6 gap-3">
        {[
          { label: 'Total Assets', value: stats.total || 0, icon: Package, color: 'text-slate-900' },
          { label: 'Active', value: stats.active || 0, icon: CheckCircle, color: 'text-emerald-600' },
          { label: 'In Maintenance', value: stats.maintenance || 0, icon: Wrench, color: 'text-amber-600' },
          { label: 'Retired/Lost', value: stats.retired || 0, icon: XCircle, color: 'text-slate-500' },
          { label: 'Maintenance Due', value: stats.maintenance_due || 0, icon: Clock, color: 'text-red-600' },
          { label: 'Total Value', value: `$${(stats.total_value || 0).toLocaleString()}`, icon: DollarSign, color: 'text-blue-600' },
        ].map(s => (
          <Card key={s.label}><CardContent className="p-3 flex items-center gap-3">
            <s.icon className={`w-8 h-8 ${s.color} opacity-20`} />
            <div><p className={`text-xl font-bold ${s.color}`}>{s.value}</p><p className="text-[10px] text-muted-foreground uppercase">{s.label}</p></div>
          </CardContent></Card>
        ))}
      </div>

      {/* Alerts Panel */}
      {showAlerts && alerts.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Asset Alerts ({alerts.length})</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {alerts.slice(0, 10).map((a, i) => (
              <div key={i} className="flex items-center justify-between text-xs py-1 border-b last:border-0">
                <div className="flex items-center gap-2">
                  <Badge variant={a.severity === 'critical' ? 'destructive' : 'outline'} className="text-[10px]">{a.severity}</Badge>
                  <span className="font-medium">{a.asset_name}</span>
                </div>
                <span className="text-muted-foreground">{a.message}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search assets..." className="pl-8" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} data-testid="asset-search" />
        </div>
        <Select value={categoryFilter} onValueChange={v => { setCategoryFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={conditionFilter} onValueChange={v => { setConditionFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Condition" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Conditions</SelectItem>{CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={6} columns={8} testid="assets-loading" />
          ) : assets.length === 0 ? (
            <EmptyState
              icon={Package}
              title="No assets found"
              description={canCreate ? 'Add your first asset to start tracking inventory across properties and locations.' : 'No assets match your current filters.'}
              action={canCreate ? <Button size="sm" onClick={() => { setEditAsset(null); setFormOpen(true); }}><Plus className="w-4 h-4 mr-1" />Add First Asset</Button> : null}
              testid="assets-empty"
            />
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Asset</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Condition</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Value</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assets.map(a => (
                <TableRow key={a.id} className="hover:bg-muted/50 cursor-pointer" data-testid={`asset-row-${a.id}`}>
                  <TableCell className="font-medium" onClick={() => handleView(a)}>{a.asset_name}</TableCell>
                  <TableCell className="font-mono text-xs" onClick={() => handleView(a)}>{a.asset_code}</TableCell>
                  <TableCell onClick={() => handleView(a)}><Badge variant="outline" className="text-xs">{a.category}</Badge></TableCell>
                  <TableCell onClick={() => handleView(a)}><Badge className={`${STATUS_COLORS[a.status] || ''} border text-xs`}>{a.status}</Badge></TableCell>
                  <TableCell onClick={() => handleView(a)}><Badge className={`${COND_COLORS[a.condition] || ''} text-xs`}>{a.condition}</Badge></TableCell>
                  <TableCell onClick={() => handleView(a)} className="text-xs">{a.property_name || a.location_type || '—'}</TableCell>
                  <TableCell onClick={() => handleView(a)} className="font-mono text-xs">{a.current_value ? `$${a.current_value.toLocaleString()}` : '—'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <button onClick={() => handleView(a)} className="p-1.5 rounded hover:bg-slate-100" title="View"><Eye className="w-3.5 h-3.5 text-slate-500" /></button>
                      {canEdit && <button onClick={() => handleEdit(a)} className="p-1.5 rounded hover:bg-slate-100" title="Edit"><Edit3 className="w-3.5 h-3.5 text-slate-500" /></button>}
                      {canDelete && <button onClick={() => handleDelete(a.id, a.asset_name)} className="p-1.5 rounded hover:bg-red-50" title="Delete"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
          {total > 50 && (
            <div className="flex items-center justify-between px-4 py-2 border-t">
              <p className="text-xs text-muted-foreground">Showing {(page - 1) * 50 + 1}–{Math.min(page * 50, total)} of {total}</p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                <Button variant="outline" size="sm" disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <AssetFormDialog open={formOpen} onClose={() => { setFormOpen(false); setEditAsset(null); }} onSaved={fetchData} asset={editAsset} houses={houses} participants={participants} staff={staffList} />
      <AssetDetailDrawer asset={detailAsset} open={detailOpen} onClose={() => setDetailOpen(false)} onUpdate={fetchData} permissions={permissions} />
    </div>
  );
}
